import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { PalestineFlag } from "./Reveal";
import { BrandLogo } from "./BrandLogo";

const links = (L) => [
  { to: "/shop", label: L.nav.shopAll },
  { to: "/shop?collection=classiques", label: L.nav.classics },
  { to: "/shop?collection=melanges", label: L.nav.blended },
  { to: "/shop?collection=fruite", label: L.nav.fruity },
  { to: "/story", label: L.nav.story },
  { to: "/mission", label: L.nav.mission },
  { to: "/contact", label: L.nav.contact },
];

export const Navbar = () => {
  const { L, lang, setLang, count, setCartOpen } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="bg-gold text-obsidian text-center py-1.5 px-4 text-[0.65rem] tracking-[0.25em] font-medium uppercase" data-testid="announcement-bar">
        {L.nav.announce}
      </div>
      <div className={"transition-all duration-500 " + (scrolled ? "bg-obsidian/90 backdrop-blur-md border-b border-gold/15" : "bg-gradient-to-b from-obsidian/80 to-transparent")}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[4.5rem] flex items-center justify-between gap-4">
          <button data-testid="logo-home-button" onClick={() => navigate("/")} className="shrink-0" aria-label="ARIJ MISK">
            <BrandLogo emblemClass="h-9" textClass="text-[0.6rem]" testid="header-logo" />
          </button>

          <nav className="hidden min-[1280px]:flex items-center gap-5">
            {links(L).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={"nav-link-" + l.to.replace(/[/?=]/g, "-")}
                className="text-[0.7rem] tracking-[0.22em] uppercase text-ivory/80 hover:text-gold transition-colors whitespace-nowrap"
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/mission" data-testid="palestine-badge" className="hidden md:flex min-[1280px]:hidden items-center gap-2 border border-gold/30 rounded-full px-3 py-1.5 hover:border-gold/80 transition-colors">
              <PalestineFlag className="w-4 h-3 rounded-[2px]" />
              <span className="text-[0.6rem] tracking-[0.18em] text-ivory/90 whitespace-nowrap">{L.nav.badge}</span>
            </Link>

            <div className="flex shrink-0 items-center border border-gold/25 rounded-full overflow-hidden" data-testid="lang-toggle">
              {[["fr", "FR"], ["en", "EN"], ["ar", "عربي"]].map(([l, label]) => (
                <button
                  key={l}
                  data-testid={"lang-" + l}
                  onClick={() => setLang(l)}
                  className={"px-2.5 py-1 text-[0.65rem] uppercase transition-colors " + (lang === l ? "bg-gold text-obsidian" : "text-ivory/70 hover:text-gold")}
                >
                  {label}
                </button>
              ))}
            </div>

            <button data-testid="cart-open-button" onClick={() => setCartOpen(true)} className="relative p-2 text-ivory hover:text-gold transition-colors" aria-label={L.nav.cart}>
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-gold text-obsidian text-[0.6rem] font-semibold w-4 h-4 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>

            <button data-testid="mobile-menu-button" className="min-[1280px]:hidden p-2 text-ivory" onClick={() => setOpen(true)} aria-label={L.nav.menu}>
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-50 bg-obsidian star-lattice flex flex-col" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} data-testid="mobile-menu">
            <div className="flex items-center justify-between px-5 h-24">
              <BrandLogo emblemClass="h-12" textClass="text-[0.7rem]" testid="mobile-logo" />
              <button data-testid="mobile-menu-close-button" onClick={() => setOpen(false)} aria-label={L.nav.close} className="p-2 text-ivory">
                <X className="w-7 h-7" strokeWidth={1.25} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-1.5 px-8">
              {links(L).map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i, duration: 0.45 }}>
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={"mobile-nav-link-" + l.to.replace(/[/?=]/g, "-")}
                    className="block font-display text-3xl text-ivory py-1.5 hover:text-gold transition-colors"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10 flex items-center justify-between">
              <Link to="/mission" onClick={() => setOpen(false)} className="flex items-center gap-2 text-[0.6rem] tracking-[0.18em] text-ivory/80" data-testid="mobile-palestine-badge">
                <PalestineFlag className="w-4 h-3 rounded-[2px]" />
                {L.nav.badge}
              </Link>
              <div className="flex items-center border border-gold/25 rounded-full overflow-hidden">
                {[["fr", "FR"], ["en", "EN"], ["ar", "عربي"]].map(([l, label]) => (
                  <button
                    key={l}
                    data-testid={"mobile-lang-" + l}
                    onClick={() => setLang(l)}
                    className={"px-3 py-1.5 text-[0.65rem] uppercase " + (lang === l ? "bg-gold text-obsidian" : "text-ivory/70")}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
